import { usePopover } from '@/hooks/usePopover';
import { useFileDownload } from '@/hooks/useFileDownload';
import { useToast } from '@/hooks/useToast';
import { capitalize } from '@/utils/capitalize';
import { DownloadFormat, downloadSpreadsheet } from '@/utils/downloadUtils';
import { getFilename } from '@/utils/path';
import { CircularProgress, Divider, IconButton, ListItemIcon, Tooltip, useTheme } from '@mui/material';
import { FileDownloadOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { useCallback, useState } from 'react';
import { StyledList, StyledListHeader, StyledListItem } from '../AssetsPage/PopoverList';
import { BodyText } from '../BodyText';
import { RoundedPopover } from '../Popover/RoundedPopover';
import { SelectedFilters } from '../FilterData/types';
import { MatchAsset } from './SearchTable';
import { SearchType } from './types';
import { normalizeSelectedFilters } from './utils';

export const formatSearchResultsFilename = (
  searchAssetPath: string | undefined,
  searchType: SearchType | undefined,
  format: DownloadFormat
): string => {
  const capitalizedSearchType = searchType ? capitalize(searchType) : undefined;
  let baseName = 'Search Results';
  if (searchAssetPath) {
    const assetFilename = getFilename(searchAssetPath);
    const lastDotIndex = assetFilename.lastIndexOf('.');
    const assetNameWithoutExtension = lastDotIndex === -1 ? assetFilename : assetFilename.slice(0, lastDotIndex);
    // eslint-disable-next-line unicorn/prefer-string-replace-all -- replaceAll requires ES2021, but TypeScript target is ES2020
    const sanitizedAssetName = assetNameWithoutExtension.replace(/[\\/:*?"<>|]/g, '_');
    baseName = `${sanitizedAssetName} - ${capitalizedSearchType || 'Search'} Search Results`;
  } else if (capitalizedSearchType) {
    baseName = `${capitalizedSearchType} Search Results`;
  }
  const today = new Date().toISOString().split('T')[0];
  return `${baseName} ${today}.${format}`;
};

type AssetSearchExportType = 'geometric' | 'part' | 'visual';

interface SearchResultsDownloadProperties {
  /** Used for the client-side download fallback when the searchType has no server-side export. */
  fetchAllResults: () => Promise<MatchAsset[]>;
  disabled?: boolean;
  searchAssetPath?: string;
  searchAssetId?: string;
  searchQuery?: string;
  searchType?: SearchType;
  /** Tenant in which to run the export search. Required for server-side path. */
  searchTenantId?: string;
  /** Tenant that owns the source asset. Only set when different from `searchTenantId` (cross-tenant). */
  assetTenantId?: string;
  selectedFilters?: SelectedFilters;
  /** Match-percentage threshold for geometric and part searches. */
  minThreshold?: number;
  /** Size threshold for visual searches. */
  sizeThreshold?: number;
}

export const SearchResultsDownload = ({
  fetchAllResults,
  disabled,
  searchAssetPath,
  searchAssetId,
  searchQuery,
  searchType,
  searchTenantId,
  assetTenantId,
  selectedFilters,
  minThreshold,
  sizeThreshold,
}: SearchResultsDownloadProperties) => {
  const { showToast, hideToast } = useToast();
  const theme = useTheme();
  const [isClientDownloading, setIsClientDownloading] = useState(false);
  const { download, isDownloading: isServerDownloading } = useFileDownload();

  const {
    open: openDownload,
    close: closeDownload,
    isOpen: isDownloadOpen,
    anchorElement: downloadAnchorElement,
  } = usePopover();

  const isDownloading = isClientDownloading || isServerDownloading;
  const assetExportType = getAssetExportType(searchType);
  const useAssetServerSide = !!(assetExportType && searchTenantId && searchAssetId);
  const useTextServerSide = searchType === 'text' && !!searchTenantId;

  const handleTextServerDownload = useCallback(
    async (format: DownloadFormat) => {
      closeDownload();
      // Held open: the default six seconds expires while a wide tenant is still building.
      showToast({ message: `Preparing your ${format.toUpperCase()} download`, type: 'info', persist: true });
      try {
        await download(`/tenants/${searchTenantId!}/assets/text-search/file?format=${format}`, {
          filename: formatSearchResultsFilename(searchAssetPath, searchType, format),
          errorMessage: `Failed to download ${format.toUpperCase()}`,
          method: 'POST',
          body: { searchQuery, filters: normalizeSelectedFilters(selectedFilters) },
        });
      } finally {
        hideToast();
      }
    },
    [
      searchTenantId,
      searchAssetPath,
      searchType,
      searchQuery,
      selectedFilters,
      download,
      closeDownload,
      showToast,
      hideToast,
    ]
  );

  const handleServerDownload = useCallback(
    async (format: DownloadFormat, exportType: AssetSearchExportType) => {
      const filename = formatSearchResultsFilename(searchAssetPath, searchType, format);
      const path = buildExportPath({
        searchTenantId: searchTenantId!,
        assetId: searchAssetId!,
        exportType,
        format,
        assetTenantId: assetTenantId === searchTenantId ? undefined : assetTenantId,
      });
      const body = buildExportBody({ exportType, searchQuery, selectedFilters, minThreshold, sizeThreshold });
      closeDownload();
      await download(path, {
        filename,
        errorMessage: `Failed to download ${format.toUpperCase()}`,
        method: 'POST',
        body,
      });
    },
    [
      searchAssetPath,
      searchType,
      searchTenantId,
      searchAssetId,
      assetTenantId,
      searchQuery,
      selectedFilters,
      minThreshold,
      sizeThreshold,
      download,
      closeDownload,
    ]
  );

  const handleClientDownload = useCallback(
    async (format: DownloadFormat) => {
      try {
        setIsClientDownloading(true);
        closeDownload();
        const results = await fetchAllResults();

        if (results.length === 0) {
          showToast({ message: 'No search results to download', type: 'info' });
          return;
        }

        const hasSearchQuery = !!searchQuery;
        const contextHeaders: string[] = [];
        const contextCell: string[] = [];
        if (searchAssetPath && searchAssetId) {
          contextHeaders.push('Asset Path', 'Asset ID');
          contextCell.push(searchAssetPath, searchAssetId);
        }
        if (hasSearchQuery) {
          contextHeaders.push('Search Query');
          contextCell.push(searchQuery);
        }

        const headers = [...contextHeaders, 'Match Path', 'Match ID'];
        const rows = results.map(matchAsset => [...contextCell, matchAsset.path || '', matchAsset.id || '']);
        const data = [headers, ...rows];

        const filename = formatSearchResultsFilename(searchAssetPath, searchType, format);
        downloadSpreadsheet(data, { filename, format });
        showToast({ message: `Downloaded ${results.length} results as ${format.toUpperCase()}`, type: 'success' });
      } catch {
        showToast({ message: `Failed to download ${format.toUpperCase()}`, type: 'error' });
      } finally {
        setIsClientDownloading(false);
      }
    },
    [fetchAllResults, showToast, searchAssetPath, searchAssetId, searchQuery, searchType, closeDownload]
  );

  const handleDownload = (format: DownloadFormat) => {
    if (useAssetServerSide && assetExportType) {
      void handleServerDownload(format, assetExportType);
      return;
    }
    if (useTextServerSide) {
      void handleTextServerDownload(format);
      return;
    }
    void handleClientDownload(format);
  };

  const formats: DownloadFormat[] = ['csv', 'xlsx'];
  const formatLabels: Record<DownloadFormat, string> = {
    csv: 'CSV',
    xlsx: 'XLSX',
    xls: 'XLS',
  };

  return (
    <>
      <Tooltip title='Download Search Results' placement='bottom'>
        <span>
          <IconButton
            size='small'
            onClick={openDownload}
            disabled={disabled || isDownloading}
            sx={{
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {isDownloading ? <CircularProgress size={18} /> : <FileDownloadOutlined fontSize='small' />}
          </IconButton>
        </span>
      </Tooltip>
      <RoundedPopover
        open={isDownloadOpen}
        anchorEl={downloadAnchorElement}
        onClose={closeDownload}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '4px',
              borderRadius: '12px',
            },
          },
        }}
      >
        <StyledList>
          <StyledListHeader>
            <BodyText>Download Search Results</BodyText>
          </StyledListHeader>
          <Divider />
          {formats.map(format => (
            <StyledListItem
              key={format}
              onClick={() => handleDownload(format)}
              sx={{ cursor: 'pointer' }}
              data-testid={`search-results-download-${format}-option`}
            >
              <ListItemIcon>
                <InsertDriveFileOutlined color='action' sx={{ fontSize: '18px' }} />
              </ListItemIcon>
              <BodyText>{formatLabels[format]}</BodyText>
            </StyledListItem>
          ))}
        </StyledList>
      </RoundedPopover>
    </>
  );
};

function getAssetExportType(searchType: SearchType | undefined): AssetSearchExportType | undefined {
  if (searchType === 'geometric' || searchType === 'part' || searchType === 'visual') {
    return searchType;
  }
  return undefined;
}

function buildExportPath(parameters: {
  searchTenantId: string;
  assetId: string;
  exportType: AssetSearchExportType;
  format: DownloadFormat;
  assetTenantId?: string;
}): string {
  const queryParameters = new URLSearchParams({ format: parameters.format });
  if (parameters.assetTenantId) {
    queryParameters.set('assetTenantId', parameters.assetTenantId);
  }
  return `/tenants/${parameters.searchTenantId}/assets/${parameters.assetId}/${parameters.exportType}-search/file?${queryParameters.toString()}`;
}

function buildExportBody(parameters: {
  exportType: AssetSearchExportType;
  searchQuery?: string;
  selectedFilters?: SelectedFilters;
  minThreshold?: number;
  sizeThreshold?: number;
}): Record<string, unknown> {
  const body: Record<string, unknown> = {
    searchQuery: parameters.searchQuery,
    filters: normalizeSelectedFilters(parameters.selectedFilters),
  };
  if (parameters.exportType === 'geometric' || parameters.exportType === 'part') {
    body.minThreshold = parameters.minThreshold ?? 0;
  } else {
    body.sizeThreshold = parameters.sizeThreshold;
  }
  return body;
}
